import { useState, useCallback, useRef } from 'react';
import { CalendarEvent } from '../types';
import { calendarService } from '../services/supabase';
import { googleCalendarService } from '../services/googleCalendar';
import { useQuota } from '../context/QuotaContext';
import { useOptimisticCRUD, createOptimisticItem } from './useOptimisticCRUD';

export interface UseEventsOptions {
  /** Optional: called when an update fails (e.g. show toast) */
  onUpdateFailed?: () => void;
}

export function useEvents(options: UseEventsOptions = {}) {
  const { onUpdateFailed } = options;
  const { showQuotaLimit } = useQuota();

  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const rangeRef = useRef<{ start: Date; end: Date } | null>(null);
  /** Keep googleEventId for deletes (row may already be gone when sync runs). */
  const googleEventIdByIdRef = useRef<Map<string, string>>(new Map());

  const rememberGoogleIds = useCallback((list: CalendarEvent[]) => {
    for (const e of list) {
      if (e.googleEventId) googleEventIdByIdRef.current.set(e.id, e.googleEventId);
      else googleEventIdByIdRef.current.delete(e.id);
    }
  }, []);

  const loadEventsInRange = useCallback(async (start: Date, end: Date) => {
    rangeRef.current = { start, end };
    try {
      const next = await calendarService.getInRange(start, end);
      rememberGoogleIds(next);
      setEvents(next);
    } catch (err) {
      console.error('Error loading events in range:', err);
    }
  }, [rememberGoogleIds]);

  const refresh = useCallback(() => {
    const range = rangeRef.current;
    if (range) {
      loadEventsInRange(range.start, range.end);
    }
  }, [loadEventsInRange]);

  const addItem = useCallback((item: CalendarEvent) => {
    setEvents((prev) => {
      if (prev.some((e) => e.id === item.id)) return prev;
      return [item, ...prev];
    });
  }, []);
  const updateItem = useCallback((item: CalendarEvent) => {
    setEvents((prev) => prev.map((e) => (e.id === item.id ? item : e)));
  }, []);
  const removeItem = useCallback((id: string) => {
    setEvents((prev) => prev.filter((e) => e.id !== id));
  }, []);
  const replaceItem = useCallback((tempId: string, realItem: CalendarEvent) => {
    setEvents((prev) => prev.map((e) => (e.id === tempId ? realItem : e)));
  }, []);

  const crud = useOptimisticCRUD<CalendarEvent, Omit<CalendarEvent, 'id'>>({
    onOptimisticAdd: addItem,
    onOptimisticUpdate: updateItem,
    onOptimisticRemove: removeItem,
    onOptimisticReplace: replaceItem,
    service: {
      create: async (data) => {
        const result = (await calendarService.create(data)) as any;
        if (result?.error === 'QUOTA_LIMIT') {
          showQuotaLimit(result.quotaInfo);
          return null;
        }
        if (result && !result.error) {
          googleCalendarService.pushUpsert(result.id);
          return result;
        }
        return null;
      },
      update: async (id, data) => {
        const result = await calendarService.update(id, data as CalendarEvent);
        if (result) googleCalendarService.pushUpsert(result.id);
        return result;
      },
      delete: async (id) => {
        const googleEventId = googleEventIdByIdRef.current.get(id);
        // Sync delete while the row (and google_event_id) may still exist
        try {
          await googleCalendarService.syncDelete({ eventId: id, googleEventId });
        } catch {
          /* not connected / offline — local delete still proceeds */
        }
        const ok = await calendarService.delete(id);
        googleEventIdByIdRef.current.delete(id);
        return ok;
      },
    },
    onRollback: refresh,
    onUpdateFailed,
  });

  const addEvent = useCallback(
    (event: Omit<CalendarEvent, 'id'>) => {
      const optimistic = createOptimisticItem<CalendarEvent>(
        event as Omit<CalendarEvent, 'id' | 'createdAt' | 'updatedAt'>
      );
      crud.create(event, optimistic);
    },
    [crud]
  );

  const updateEvent = useCallback(
    (event: CalendarEvent, updateOptions?: { previousItem?: CalendarEvent }) => {
      crud.update(event.id, { ...event, updatedAt: new Date() }, updateOptions);
    },
    [crud]
  );

  const deleteEvent = useCallback(
    (id: string) => {
      crud.remove(id);
    },
    [crud]
  );

  return {
    events,
    loadEventsInRange,
    addEvent,
    updateEvent,
    deleteEvent,
  };
}
